import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  Filter,
  Download,
  Share2,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Grid,
  List,
  Eye,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Tag,
  Link as LinkIcon,
  Image as ImageIcon,
  FileText,
  ExternalLink,
  Sparkles,
  TrendingUp,
  BookOpen,
  Search,
  RefreshCw,
  Globe,
  Inbox,
  Wand2,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { ConfirmDialog } from '@/components/ui/Modal';
import { api } from '@/services/client';
import { tabService } from '@/services/tabs';
import { useTabMonitorSession } from '@/context/TabMonitorSessionContext';
import { toast } from 'sonner';

interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  timestamp: Date;
  type: 'visit' | 'bookmark' | 'search' | 'milestone' | 'custom';
  url?: string;
  favicon?: string;
  thumbnail?: string;
  tags: string[];
  duration?: number;
  relatedEvents?: string[];
  metadata?: {
    domain?: string;
    pageTitle?: string;
    visits?: number;
    importance?: number;
  };
}

interface TimelineCluster {
  id: string;
  name: string;
  events: TimelineEvent[];
  startTime: Date;
  endTime: Date;
  theme?: string;
  color?: string;
}

type ViewMode = 'timeline' | 'grid' | 'list';
type TimeScale = 'hour' | 'day' | 'week' | 'month';

function deriveTagsFromUrl(url: string): string[] {
  try {
    const u = new URL(url);
    const domain = u.hostname.replace(/^www\./, '');
    const parts = domain.split('.');
    // Use the registrable-ish label as a tag.
    const main = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
    const tags: string[] = [];
    if (main) tags.push(main.toLowerCase());
    if (u.pathname && u.pathname !== '/') {
      const first = u.pathname.split('/').filter(Boolean)[0];
      if (first && first.length <= 24) tags.push(first.toLowerCase());
    }
    return Array.from(new Set(tags));
  } catch {
    return [];
  }
}

const CLUSTER_COLORS = [
  'bg-primary-500',
  'bg-purple-500',
  'bg-success-500',
  'bg-warning-500',
  'bg-pink-500',
  'bg-indigo-500',
];

function getColorForTag(tag: string): string {
  const index = tag
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return CLUSTER_COLORS[index % CLUSTER_COLORS.length];
}

export function TimelineBuilder() {
  const navigate = useNavigate();
  const { workspaceSessionId } = useTabMonitorSession();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [clusters, setClusters] = useState<TimelineCluster[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [timeScale, setTimeScale] = useState<TimeScale>('day');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    end: new Date()
  });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [editingEvent, setEditingEvent] = useState<TimelineEvent | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const buildClusters = useCallback((eventList: TimelineEvent[]): TimelineCluster[] => {
    if (eventList.length === 0) return [];
    const groups = new Map<string, TimelineEvent[]>();

    // Group by the first derived tag (typically the registrable domain).
    // Falls back to the URL host if no tags are present.
    for (const ev of eventList) {
      const key = ev.tags[0] || ev.metadata?.domain || 'other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(ev);
    }

    const clusters: TimelineCluster[] = [];
    groups.forEach((groupEvents, key) => {
      if (groupEvents.length < 2) return; // skip singletons — no journey
      const sorted = [...groupEvents].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
      );
      clusters.push({
        id: `cluster-${key}`,
        name: `${key} Research Journey`,
        events: sorted,
        startTime: sorted[0].timestamp,
        endTime: sorted[sorted.length - 1].timestamp,
        theme: key,
        color: getColorForTag(key),
      });
    });

    return clusters.sort(
      (a, b) => b.events.length - a.events.length
    );
  }, []);

  const loadTimelineData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      let sid = workspaceSessionId;
      if (!sid) {
        const sessionResponse = await api.get<{ sessions: { id: string }[] }>('/sessions');
        const sessions = sessionResponse.data?.sessions ?? [];
        sid = sessions[0]?.id ?? null;
      }
      if (!sid) {
        setEvents([]);
        setClusters([]);
        setActiveSessionId(null);
        return;
      }
      setActiveSessionId(sid);

      const tabsResp = await tabService.getTabsBySession(sid, 1, 200);
      const tabs = tabsResp.tabs ?? [];
      const now = Date.now();
      const rangeStart = dateRange.start.getTime();
      const rangeEnd = dateRange.end.getTime();

      const timelineEvents: TimelineEvent[] = tabs
        .filter((t) => {
          const ts = t.last_visited_at || t.created_at;
          if (!ts) return true;
          const ms = new Date(ts).getTime();
          if (Number.isNaN(ms)) return true;
          return ms >= rangeStart && ms <= rangeEnd;
        })
        .map((tab, index) => {
          const url = tab.url ?? '';
          const tags = deriveTagsFromUrl(url);
          return {
            id: tab.id ? String(tab.id) : `evt-${index}`,
            title: tab.title || tab.url || '(untitled tab)',
            description: url ? `Visited ${url}` : 'Tab visit',
            timestamp: new Date(
              tab.last_visited_at || tab.created_at || now
            ),
            type: 'visit' as const,
            url,
            favicon: tab.favicon,
            tags,
            metadata: { domain: tab.domain, pageTitle: tab.title },
          };
        })
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

      setEvents(timelineEvents);
      setClusters(buildClusters(timelineEvents));
      setAiSuggestions(buildAiSuggestions(timelineEvents));
    } catch (err) {
      console.error('Error loading timeline data:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load timeline');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceSessionId, dateRange.start, dateRange.end, buildClusters]);

  useEffect(() => {
    void loadTimelineData();
  }, [loadTimelineData]);

  const buildAiSuggestions = (eventList: TimelineEvent[]): string[] => {
    if (eventList.length === 0) return [];
    const domains = new Set(
      eventList
        .map((e) => e.metadata?.domain)
        .filter((d): d is string => !!d)
    );
    const recent = eventList.slice(-5);
    return [
      domains.size > 1
        ? `Group events by ${domains.size} unique domains`
        : 'Tag clusters by domain for quick filtering',
      recent.length > 0
        ? `Highlight the ${recent.length} most recent visits`
        : 'Highlight the most recent visits',
      eventList.length >= 4
        ? 'Connect related research paths into a study plan'
        : 'Capture more research tabs to enable AI grouping',
      'Export as study timeline (CSV / JSON)',
      'Auto-summarize clusters into a one-line takeaway',
    ];
  };

  const handleGenerateTimeline = async () => {
    if (!activeSessionId) {
      toast.error('No active workspace session — sync tabs from the extension first');
      return;
    }
    if (events.length === 0) {
      toast.error('No tabs in the current date range to build a timeline from');
      return;
    }
    setIsGenerating(true);
    try {
      const tabIds = events.map((e) => e.id).filter((id) => !id.startsWith('evt-'));
      const result = await api.post<{ id?: string; app_id?: string }>('/apps', {
        session_id: activeSessionId,
        tab_ids: tabIds,
        app_type: 'timeline',
        app_name: `Timeline — ${new Date().toLocaleDateString()}`,
      });
      const newId = result.data?.id ?? result.data?.app_id;
      toast.success('Timeline app generated');
      if (newId) navigate(`/apps/${newId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Generation failed';
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddEvent = () => {
    setEditingEvent({
      id: `custom-${Date.now()}`,
      title: '',
      description: '',
      timestamp: new Date(),
      type: 'custom',
      tags: []
    });
    setShowEventEditor(true);
  };

  const handleSaveEvent = (event: TimelineEvent) => {
    if (events.find(e => e.id === event.id)) {
      setEvents(events.map(e => e.id === event.id ? event : e));
    } else {
      setEvents([...events, event]);
    }
    setShowEventEditor(false);
    setEditingEvent(null);
  };

  const handleDeleteEvent = (eventId: string) => {
    setSelectedEventId(eventId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteEvent = () => {
    if (!selectedEventId) return;
    setEvents(events.filter(e => e.id !== selectedEventId));
    setSelectedEvents(selectedEvents.filter(id => id !== selectedEventId));
    setShowDeleteConfirm(false);
    setSelectedEventId(null);
  };

  const handleExport = () => {
    const exportData = {
      events: events.filter(e => selectedEvents.length === 0 || selectedEvents.includes(e.id)),
      clusters,
      dateRange,
      generatedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timeline-${Date.now()}.json`;
    a.click();
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = searchQuery === '' || 
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTags = filterTags.length === 0 || 
      filterTags.some(tag => event.tags.includes(tag));
    
    const matchesDateRange = 
      event.timestamp >= dateRange.start && 
      event.timestamp <= dateRange.end;
    
    return matchesSearch && matchesTags && matchesDateRange;
  });

  const allTags = Array.from(new Set(events.flatMap(e => e.tags)));

  return (
    <PageLayout
      title="Timeline Builder"
      description="Create visual timelines from your browsing history and events"
      icon={Calendar}
    >
      {/* Header */}
      <div className="border-b border-khiimori-border bg-khiimori-bg-secondary/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleGenerateTimeline}
                disabled={isGenerating}
                className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    AI Generate
                  </>
                )}
              </button>
              
              <button
                onClick={handleAddEvent}
                className="px-4 py-2 bg-khiimori-bg-elevated/80 rounded-lg hover:bg-khiimori-bg-elevated transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Event
              </button>
              
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-khiimori-bg-elevated/80 rounded-lg hover:bg-khiimori-bg-elevated transition-all flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-khiimori-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search events..."
                className="w-full pl-10 pr-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* View Mode */}
            <div className="flex items-center gap-1 bg-khiimori-bg-elevated/50 rounded-lg p-1">
              <button
                onClick={() => setViewMode('timeline')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'timeline' ? 'bg-primary-500' : 'hover:bg-khiimori-bg-elevated/80'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'grid' ? 'bg-primary-500' : 'hover:bg-khiimori-bg-elevated/80'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'list' ? 'bg-primary-500' : 'hover:bg-khiimori-bg-elevated/80'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Time Scale */}
            <select
              value={timeScale}
              onChange={(e) => setTimeScale(e.target.value as TimeScale)}
              className="px-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="hour">Hourly</option>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>

            {/* Zoom */}
            <div className="flex items-center gap-2 bg-khiimori-bg-elevated/50 rounded-lg px-3 py-2">
              <button
                onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
                className="hover:text-purple-400 transition-colors"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm min-w-12 text-center">{Math.round(zoomLevel * 100)}%</span>
              <button
                onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.25))}
                className="hover:text-purple-400 transition-colors"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3 space-y-6">
            {/* AI Suggestions */}
            <div className="glass-card rounded-xl p-4 border border-khiimori-border">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                AI Suggestions
              </h3>
              <div className="space-y-2">
                {aiSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    className="w-full text-left text-sm p-2 rounded-lg bg-khiimori-bg-elevated/50 hover:bg-khiimori-bg-elevated/80 transition-all"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            {/* Clusters */}
            <div className="glass-card rounded-xl p-4 border border-khiimori-border">
              <h3 className="text-sm font-semibold mb-3">Research Clusters</h3>
              <div className="space-y-2">
                {clusters.map(cluster => (
                  <div
                    key={cluster.id}
                    className="p-3 rounded-lg bg-khiimori-bg-elevated/50 hover:bg-khiimori-bg-elevated/80 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-3 h-3 rounded-full ${cluster.color}`} />
                      <span className="text-sm font-medium">{cluster.name}</span>
                    </div>
                    <div className="text-xs text-khiimori-text-muted">
                      {cluster.events.length} events
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tag Filter */}
            <div className="glass-card rounded-xl p-4 border border-khiimori-border">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter by Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => {
                      setFilterTags(prev =>
                        prev.includes(tag)
                          ? prev.filter(t => t !== tag)
                          : [...prev, tag]
                      );
                    }}
                    className={`px-3 py-1 rounded-full text-xs transition-all ${
                      filterTags.includes(tag)
                        ? 'bg-primary-500 text-white'
                        : 'bg-khiimori-bg-elevated/80 hover:bg-khiimori-bg-elevated'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="glass-card rounded-xl p-4 border border-khiimori-border">
              <h3 className="text-sm font-semibold mb-3">Statistics</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-khiimori-text-muted">Total Events</span>
                  <span className="font-semibold">{events.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-khiimori-text-muted">Filtered</span>
                  <span className="font-semibold">{filteredEvents.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-khiimori-text-muted">Clusters</span>
                  <span className="font-semibold">{clusters.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-khiimori-text-muted">Selected</span>
                  <span className="font-semibold">{selectedEvents.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            {viewMode === 'timeline' && (
              <TimelineView
                events={filteredEvents}
                selectedEvents={selectedEvents}
                onSelectEvent={(id) => {
                  setSelectedEvents(prev =>
                    prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
                  );
                }}
                onEditEvent={(event) => {
                  setEditingEvent(event);
                  setShowEventEditor(true);
                }}
                onDeleteEvent={handleDeleteEvent}
                zoomLevel={zoomLevel}
                isLoading={isLoading}
                loadError={loadError}
              />
            )}

            {viewMode === 'grid' && (
              <GridView
                events={filteredEvents}
                selectedEvents={selectedEvents}
                onSelectEvent={(id) => {
                  setSelectedEvents(prev =>
                    prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
                  );
                }}
                onEditEvent={(event) => {
                  setEditingEvent(event);
                  setShowEventEditor(true);
                }}
                onDeleteEvent={handleDeleteEvent}
              />
            )}

            {viewMode === 'list' && (
              <ListView
                events={filteredEvents}
                selectedEvents={selectedEvents}
                onSelectEvent={(id) => {
                  setSelectedEvents(prev =>
                    prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
                  );
                }}
                onEditEvent={(event) => {
                  setEditingEvent(event);
                  setShowEventEditor(true);
                }}
                onDeleteEvent={handleDeleteEvent}
              />
            )}
          </div>
        </div>
      </div>

      {/* Event Editor Modal */}
      <AnimatePresence>
        {showEventEditor && editingEvent && (
          <EventEditorModal
            event={editingEvent}
            onSave={handleSaveEvent}
            onClose={() => {
              setShowEventEditor(false);
              setEditingEvent(null);
            }}
          />
        )}
      </AnimatePresence>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteEvent}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </PageLayout>
  );
}

// Timeline View Component
function TimelineView({ events, selectedEvents, onSelectEvent, onEditEvent, onDeleteEvent, zoomLevel, isLoading, loadError }: any) {
  if (isLoading) {
    return (
      <div className="glass-card rounded-xl border border-khiimori-border p-10">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="rounded-full bg-khiimori-bg-elevated/10 p-3">
            <RefreshCw className="h-6 w-6 animate-spin text-violet-400" />
          </div>
          <p className="text-sm font-medium text-khiimori-text-secondary">
            Loading timeline…
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="glass-card rounded-xl border border-error-500/30 p-10">
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-sm font-medium text-error-400">Couldn't load timeline</p>
          <p className="max-w-md text-xs text-khiimori-text-muted">{loadError}</p>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="glass-card rounded-xl border border-khiimori-border p-10">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="rounded-full bg-khiimori-bg-elevated/10 p-3">
            <Inbox className="h-6 w-6 text-khiimori-text-muted" />
          </div>
          <p className="text-sm font-medium text-khiimori-text-secondary">
            No tabs in the selected date range
          </p>
          <p className="max-w-md text-xs text-khiimori-text-muted">
            Sync tabs from the browser extension or widen the date range, then come back.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-xl p-6 border border-khiimori-border">
      <div className="relative" style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left' }}>
        {/* Timeline axis */}
        <div className="absolute left-12 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 via-primary-600 to-primary-500" />
        
        {/* Events */}
        <div className="space-y-6">
          {events.map((event: TimelineEvent, idx: number) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="relative pl-24"
            >
              {/* Timeline dot */}
              <div className={`absolute left-10 top-4 w-5 h-5 rounded-full border-2 ${
                selectedEvents.includes(event.id) ? 'bg-primary-500 border-primary-400' : 'bg-khiimori-bg-secondary border-primary-600'
              } cursor-pointer z-10`}
                onClick={() => onSelectEvent(event.id)}
              />
              
              {/* Event card */}
              <div className={`bg-khiimori-bg-elevated/50 rounded-lg p-4 border transition-all cursor-pointer ${
                selectedEvents.includes(event.id) ? 'border-primary-500 bg-primary-500/10' : 'border-khiimori-border hover:border-khiimori-border'
              }`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-khiimori-text-primary mb-1">{event.title}</h4>
                    <p className="text-sm text-khiimori-text-muted">{event.description}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => onEditEvent(event)}
                      className="p-1.5 hover:bg-khiimori-bg-elevated/80 rounded transition-all"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDeleteEvent(event.id)}
                      className="p-1.5 hover:bg-error-400/20 rounded transition-all"
                    >
                      <Trash2 className="w-4 h-4 text-error-400" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-xs text-khiimori-text-muted">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {event.timestamp.toLocaleString()}
                  </span>
                  {event.duration && (
                    <span>{Math.round(event.duration / 60)} min</span>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3">
                  {event.tags.map(tag => (
                    <span key={tag} className="px-2 py-1 bg-primary-500/20 text-purple-300 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Grid View Component
function GridView({ events, selectedEvents, onSelectEvent, onEditEvent, onDeleteEvent }: any) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {events.map((event: TimelineEvent, idx: number) => (
        <motion.div
          key={event.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: idx * 0.03 }}
          className={`glass-card rounded-xl p-4 border cursor-pointer transition-all ${
            selectedEvents.includes(event.id) ? 'border-primary-500 bg-primary-500/10' : 'border-khiimori-border hover:border-khiimori-border'
          }`}
          onClick={() => onSelectEvent(event.id)}
        >
          <div className="flex items-start justify-between mb-3">
            <h4 className="font-semibold text-khiimori-text-primary flex-1">{event.title}</h4>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditEvent(event);
                }}
                className="p-1.5 hover:bg-khiimori-bg-elevated/80 rounded transition-all"
              >
                <Edit className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteEvent(event.id);
                }}
                className="p-1.5 hover:bg-error-400/20 rounded transition-all"
              >
                <Trash2 className="w-3.5 h-3.5 text-error-400" />
              </button>
            </div>
          </div>
          
          <p className="text-sm text-khiimori-text-muted mb-3 line-clamp-2">{event.description}</p>
          
          <div className="flex items-center gap-2 text-xs text-khiimori-text-muted mb-3">
            <Clock className="w-3 h-3" />
            {event.timestamp.toLocaleDateString()}
          </div>
          
          <div className="flex flex-wrap gap-1.5">
            {event.tags.map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-primary-500/20 text-purple-300 rounded text-xs">
                {tag}
              </span>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// List View Component
function ListView({ events, selectedEvents, onSelectEvent, onEditEvent, onDeleteEvent }: any) {
  return (
    <div className="glass-card rounded-xl border border-khiimori-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-khiimori-bg-elevated/50 border-b border-khiimori-border">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-khiimori-text-muted">Event</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-khiimori-text-muted">Date/Time</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-khiimori-text-muted">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-khiimori-text-muted">Tags</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-khiimori-text-muted">Actions</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event: TimelineEvent, idx: number) => (
            <motion.tr
              key={event.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: idx * 0.02 }}
              className={`border-b border-khiimori-border/50 cursor-pointer transition-all ${
                selectedEvents.includes(event.id) ? 'bg-primary-500/10' : 'hover:bg-khiimori-bg-elevated/50'
              }`}
              onClick={() => onSelectEvent(event.id)}
            >
              <td className="px-4 py-4">
                <div>
                  <div className="font-medium text-khiimori-text-primary">{event.title}</div>
                  <div className="text-sm text-khiimori-text-muted line-clamp-1">{event.description}</div>
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-khiimori-text-muted">
                {event.timestamp.toLocaleString()}
              </td>
              <td className="px-4 py-4">
                <span className="px-2 py-1 bg-khiimori-bg-elevated/80 rounded text-xs capitalize">
                  {event.type}
                </span>
              </td>
              <td className="px-4 py-4">
                <div className="flex flex-wrap gap-1">
                  {event.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="px-2 py-0.5 bg-primary-500/20 text-purple-300 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                  {event.tags.length > 2 && (
                    <span className="px-2 py-0.5 bg-khiimori-bg-elevated/80 rounded text-xs">
                      +{event.tags.length - 2}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditEvent(event);
                    }}
                    className="p-1.5 hover:bg-khiimori-bg-elevated/80 rounded transition-all"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteEvent(event.id);
                    }}
                    className="p-1.5 hover:bg-error-400/20 rounded transition-all"
                  >
                    <Trash2 className="w-4 h-4 text-error-400" />
                  </button>
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Event Editor Modal Component
function EventEditorModal({ event, onSave, onClose }: any) {
  const [formData, setFormData] = useState(event);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 modal-backdrop z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-khiimori-bg-secondary rounded-2xl border border-khiimori-border max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">
              {event.id.startsWith('custom') ? 'Add Event' : 'Edit Event'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-khiimori-bg-elevated/80 rounded-lg transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Event title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 h-24 resize-none"
                placeholder="Event description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="visit">Visit</option>
                  <option value="bookmark">Bookmark</option>
                  <option value="search">Search</option>
                  <option value="milestone">Milestone</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Date/Time</label>
                <input
                  type="datetime-local"
                  value={formData.timestamp.toISOString().slice(0, 16)}
                  onChange={(e) => setFormData({ ...formData, timestamp: new Date(e.target.value) })}
                  className="w-full px-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tags (comma separated)</label>
              <input
                type="text"
                value={formData.tags.join(', ')}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map((t: string) => t.trim()) })}
                className="w-full px-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="tag1, tag2, tag3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">URL (optional)</label>
              <input
                type="url"
                value={formData.url || ''}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-4 py-2 glass-card border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-khiimori-border">
            <button
              onClick={() => onSave(formData)}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Event
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-khiimori-bg-elevated/80 rounded-lg hover:bg-khiimori-bg-elevated transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}


export default TimelineBuilder;
